<template>
  <div
      class="vModal__mask"
      :class="{'vModal__mask--embedded': inIframe}"
      data-iframe-height
  >
    <div
        ref="bg"
        class="vModal__wrapper"
        data-iframe-height
        @click="closeFromBackground"
    >
      <div
          class="vModal__container uk-width-1-1 uk-width-medium-2-3 uk-width-large-1-3"
          data-iframe-height
          :class="{'vModal__container--no-header': !showHeader, 'vModal__container--no-footer': !showFooter, 'vModal__container--absolute': modalTop > 0}"
          :style="{top: modalTop + 'px'}"
      >
        <div
            v-if="showHeader"
            class="vModal__header uk-flex uk-flex-middle uk-flex-space-between"
        >
          <slot name="header"/>
          <span
              v-if="showClose"
              class="vModal__close"
              @click="closeModal"
          >close <span class="uk-icon-close"/></span>
        </div>
        <div
            class="vModal__body"
            data-iframe-height
        >
          <slot>
            A body.
          </slot>
        </div>
        <div
            v-if="showFooter"
            class="vModal__footer"
        >
          <slot name="footer"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";

@Component
export default class Modal extends Vue {
  @Prop({default: true}) showClose!: boolean;
  @Prop({default: true}) closeOnBackgroundClick!: boolean;
  @Prop({default: true}) reposition!: boolean;

  modalTop = 0;
  observer: ResizeObserver | null = null;

  created(): void {
    if (this.reposition) {
      this.modalTop = this.$store.getters["context/iframeOffsetY"];

      window.addEventListener('resize', () => {
        if (window.innerWidth < 798) {
          this.modalTop = 0;
        }
      });
    }

    if (!this.inIframe) {
      document.body.style.overflowY = "hidden";
    }
  }

  mounted(): void {
    const container: HTMLElement = this.$el.querySelector('.vModal__container')!;

    /**
     * Calculate the offset from the element to the top of the DOM
     * @param element
     */
    const determineOffset = function (element: HTMLElement): number {
      let offset = 0;

      offset += parseInt('' + element.offsetTop, 10);
      if (element.offsetParent) {
        offset += determineOffset(element.offsetParent as HTMLElement);
      }

      return offset;
    }

    try {
      const store = this.$store;
      let resizeTimeout: number | undefined;
      this.observer = new ResizeObserver(function (e) {
        clearTimeout(resizeTimeout);

        // Wait until we've finished resizing before sending an event.
        resizeTimeout = window.setTimeout(() => {
          store.commit('context/setModalState', {
            open: true,
            height: container.offsetHeight,
            width: container.offsetWidth,
            offsetY: determineOffset(container),
          });
        }, 100);

      });
      this.observer.observe(container);
    } catch (e) {
      // Silence 'ResizeObserver loop completed with undelivered notifications.' exceptions
    }
  }

  beforeDestroy(): void {
    document.body.style.overflowY = "auto";
  }

  closeFromBackground(e: MouseEvent): void {
    if (e.target === this.$refs['bg'] && this.closeOnBackgroundClick) {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.observer?.disconnect();
    this.$store.commit('context/setModalState', {
      open: false,
      offsetY: 0,
      height: 0,
      width: 0,
    });
    this.$emit('close-modal');
  }

  get inIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  get showHeader(): boolean {
    return this.showClose || typeof this.$slots.header !== "undefined";
  }

  get showFooter(): boolean {
    return typeof this.$slots.footer !== "undefined";
  }
}
</script>

<style scoped>
.vModal__mask {
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  background-color: rgba(0, 0, 0, .6);
}

.vModal__mask--embedded {
  background-color: transparent;
  display: table;
  width: 100%;
  height: 100%;
  overflow: auto;
}

.vModal__mask--embedded__wrapper {
  display: table-cell;
  vertical-align: middle;
}

.vModal__container {
  margin: 40px auto;
  box-shadow: 0 3px 30px rgba(0, 0, 0, .4);
  border: 10px solid rgba(0, 0, 0, .4);
  border-radius: 4px;
}

.vModal__container--absolute {
  position: absolute;
  width: 80%;
  margin-left: 50%;
  transform: translateX(-50%);
}

@media only screen and (max-width: 798px) {
  .vModal__container--absolute {
    position: relative;
    transform: none;
    margin-left: auto;
  }
}

.vModal__header__slot {
  margin-left: auto;
}

.vModal__header, .vModal__body, .vModal__footer {
  background: #fff;
  padding: 1em;
}

.vModal__header, .vModal__container--no-header .vModal__body {
  border-top-left-radius: 0.4em;
  border-top-right-radius: 0.4em;
}

.vModal__header {
  background-color: #f7f7f7;
  position: relative;
}

.vModal__header h1 {
  font-size: 1.4em;
}

.vModal__footer, .vModal__container--no-footer .vModal__body {
  border-bottom-left-radius: 0.4em;
  border-bottom-right-radius: 0.4em;
}

.vModal__footer {
  background-color: #f7f7f7;
}

.vModal__close {
  --background-color: rgb(226, 226, 226);
  color: #222;
  position: absolute;
  top: -28px;
  right: 0;
  background-color: var(--background-color);
  padding: 4px 8px;
  border-radius: 0.4em;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(0, 0, 0, .2);
}

.vModal__close:hover {
  background-color: color-mix(in srgb, var(--background-color) 50%, #fff);
}

.vModal__mask .vModal__mask .vModal__container {
  border: 0;
}

.vModal__mask .vModal__mask .vModal__header, .vModal__mask .vModal__mask .vModal__body, .vModal__mask .vModal__mask .vModal__footer {
  background: #3a3a3a;
  color: #fff;
}

.vModal__mask .vModal__mask {
  background: rgba(0, 0, 0, .3);
}

@media only screen and (max-width: 798px) {
  .vModal__mask {
    position: relative;
    background: transparent;
    display: block;
    z-index: 1;
    height: auto;
  }

  .vModal__mask .vModal__mask {
    position: absolute;
    top: 0;
    height: 100%;
  }

  .vModal__mask .vModal__mask .vModal__header, .vModal__mask .vModal__mask .vModal__body, .vModal__mask .vModal__mask .vModal__footer {
    background: #3a3a3a;
    color: #fff;
  }

  .vModal__wrapper {
    width: 100%;
    display: block;
  }

  .vModal__close {
    position: relative;
    margin-left: auto;
    top: auto;
  }

  .vModal__body {
    max-height: 100%;
  }
}
</style>
